import React from 'react';
import { Link } from 'react-router-dom';

import { Spinner } from '@zillow/zrs-comet';
import { TextButton, Text, Tag } from '@zillow/constellation';

import ModalActions from 'app/shared/actions/ModalActions';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import pathsManifest from 'app/shared/pathsManifest';

const getRole = user => (user.isAdmin ? 'Company Admin' : 'User');

const roleRenderer = user => <div>{getRole(user)}</div>;

const mobileColumnConfig = {
    user: {
        width: 10,
        dataKey: 'user',
        header: 'User',
        isSortable: true,
    },
    action: {
        width: 2,
        dataKey: 'action',
        header: 'Permissions',
    },
};

const baseDesktopColumnConfig = {
    user: {
        width: 3,
        dataKey: 'user',
        header: 'User',
        isSortable: true,
    },
    role: {
        width: 3,
        dataKey: 'role',
        header: 'Role',
        renderer: roleRenderer,
    },
};

const columnConfigs = {
    getConfigs(isScreenMobile, dispatch, onResendInvite, company, yourToken, activeTab, companyUsers) {
        const desktopColumnConfig = { ...baseDesktopColumnConfig };
        const previewUserProperties = (userBrand, userToken) => {
            const onLoadProperties = () => dispatch(UserManagementActions
                .getCurrentUserProperties(userBrand, userToken));
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.USER_MANAGEMENT_PROPERTY_LIST_MODAL,
                onLoadProperties,
                header: 'Properties',
            }));
        };

        const previewPendingProperties = (email) => {
            const onLoadProperties = () => dispatch(UserManagementActions
                .getPendingUserProperties(email));
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.USER_MANAGEMENT_PROPERTY_LIST_MODAL,
                onLoadProperties,
                header: 'Properties',
            }));
        };

        const paths = pathsManifest.getAllPaths();
        const basePath = `${paths.userManagementPage}/${paths.userManagementEditUser}`;
        const toUserManagementPath = (user) => {
            const {
                userBrand,
                userToken,
                email,
                isPending,
            } = user;
            if (isPending) {
                return `${basePath}?email=${email}`;
            }
            return `${basePath}?userBrand=${userBrand}&userToken=${userToken}`;
        };

        // Renderers
        const userRenderer = user => (
            <div>
                <div>
                    <Text fontType="bodySmallHeading">{`${user.userName} `}</Text>
                    {user.userToken === yourToken && <Tag appearance="gray" size="sm">You</Tag>}
                    {user.isPending && <Tag appearance="gray" size="sm">Pending</Tag>}
                    {user.isPendingDelete && <Tag appearance="gray" size="sm">Pending Deactivation</Tag>}
                </div>
                {user.email}
                {user.isPending && (
                    <div>
                        <TextButton onClick={() => onResendInvite(user)}>
                            Resend invite
                        </TextButton>
                    </div>
                )}
            </div>
        );
        const actionRenderer = user => (user.isPendingDelete ? '-' :
            (
                <Link as="a" component={TextButton} to={toUserManagementPath(user)}>
                    {user.isCurrentUser ? 'View' : 'Edit'}
                </Link>
            ));
        const propertyRenderer = (user) => {
            if (user.isPendingDelete) {
                return '-';
            }
            const previewProperties = () => {
                if (user.isPending) {
                    previewPendingProperties(user.email);
                } else {
                    previewUserProperties(user.userBrand, user.userToken);
                }
            };
            const userId = user.isPending ? user.email : user.userToken;
            const propertyData = companyUsers.userPropertyData[userId];
            if (!propertyData) {
                return <Spinner size="sm" />;
            }
            if (propertyData.loadingState === LoadingConstants.ERROR) {
                return '-';
            }
            if (propertyData.loadingState === LoadingConstants.SUCCESS) {
                const propertyCount = propertyData.properties.length;
                return (propertyCount === 0 ?
                    propertyCount :
                    (
                        <TextButton onClick={previewProperties}>
                            {propertyCount}
                        </TextButton>
                    ));
            }
            return <Spinner size="sm" />;
        };

        if (isScreenMobile) {
            mobileColumnConfig.user.renderer = userRenderer;
            mobileColumnConfig.action.renderer = actionRenderer;
            return Object.values(mobileColumnConfig);
        }
        desktopColumnConfig.user.renderer = userRenderer;
        if (activeTab === 'users') {
            desktopColumnConfig.property = {
                width: 2,
                dataKey: 'propertyCount',
                header: 'Properties',
            };
            desktopColumnConfig.lastLogin = {
                width: 2,
                renderer: (data) => {
                    if (!data.lastActivityTimestamp) {
                        return '-';
                    }
                    const date = new Date(parseFloat(data.lastActivityTimestamp));
                    return date.toLocaleDateString();
                },
                dataKey: 'lastActivityTimestamp',
                header: 'Last Activity',
            };
            desktopColumnConfig.action = {
                width: 2,
                dataKey: 'action',
                header: 'Permissions',
            };
        } else {
            desktopColumnConfig.property = {
                width: 3,
                dataKey: 'propertyCount',
                header: 'Properties',
                align: 'center',
            };
            desktopColumnConfig.action = {
                width: 3,
                dataKey: 'action',
                header: 'Permissions',
                align: 'center',
            };
        }
        desktopColumnConfig.property.renderer = propertyRenderer;
        desktopColumnConfig.action.renderer = actionRenderer;
        return Object.values(desktopColumnConfig);
    },
};

export default columnConfigs;
